// src/pages/AdminLogin.jsx
// 修正版 - 處理 id -> userId 的對應

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, AlertCircle, Shield } from 'lucide-react';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 如果已經登入，直接跳轉到後台
  useEffect(() => {
    const token = localStorage.getItem('token');
    const adminRole = localStorage.getItem('adminRole');
    
    if (token && adminRole) {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      setError('請輸入帳號和密碼');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/admin/login`;
      
      console.log('🔍 發送登入請求:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      console.log('🔍 響應狀態:', response.status);

      const text = await response.text();
      console.log('🔍 響應內容:', text);
      
      if (!text) {
        throw new Error('伺服器返回空響應');
      }
      
      let result;
      try {
        result = JSON.parse(text);
        console.log('✅ JSON 解析成功:', result);
      } catch (e) {
        console.error('❌ JSON 解析失敗:', e);
        if (text.trim().startsWith('<')) {
          throw new Error('伺服器返回 HTML 錯誤頁面');
        }
        throw new Error('伺服器返回格式錯誤');
      }

      if (!response.ok || result.error) {
        throw new Error(result.message || '登入失敗');
      }

      // 驗證返回的數據結構
      if (!result.data || !result.data.token || !result.data.admin) {
        console.error('❌ 返回數據結構錯誤:', result);
        throw new Error('返回數據格式不正確');
      }

      console.log('✅ 登入成功');

      // 清除舊資料
      localStorage.clear();

      // 儲存資料
      // 關鍵修改：將 admin.id 儲存為 userId（對應 JWT payload 中的 user_id）
      console.log('💾 儲存登入資料...');
      
      localStorage.setItem('token', result.data.token);
      console.log('✅ Token 已儲存');
      
      // 重要：儲存 userId（對應後端 JWT payload 中的 user_id）
      localStorage.setItem('userId', result.data.admin.id);
      console.log('✅ UserId 已儲存:', result.data.admin.id);
      
      localStorage.setItem('adminRole', result.data.admin.role);
      console.log('✅ AdminRole 已儲存:', result.data.admin.role);
      
      localStorage.setItem('adminName', result.data.admin.name);
      console.log('✅ AdminName 已儲存:', result.data.admin.name);
      
      localStorage.setItem('adminUsername', result.data.admin.username);
      console.log('✅ AdminUsername 已儲存:', result.data.admin.username);
      
      // 額外儲存 email（可選）
      if (result.data.admin.email) {
        localStorage.setItem('adminEmail', result.data.admin.email);
        console.log('✅ AdminEmail 已儲存:', result.data.admin.email);
      }

      // 驗證 token payload
      try {
        const tokenParts = result.data.token.split('.');
        const payload = JSON.parse(atob(tokenParts[1]));
        console.log('🔍 Token Payload:', payload);
        
        // 驗證 user_id 和 admin.id 是否對應
        if (payload.user_id != result.data.admin.id) {
          console.warn('⚠️ Token user_id 與 admin.id 不匹配');
        } else {
          console.log('✅ Token user_id 與 admin.id 對應正確');
        }
        
        // 驗證是否為管理員 token
        if (payload.type === 'admin') {
          console.log('✅ 確認為管理員 Token');
        } else {
          console.warn('⚠️ Token type 不是 admin:', payload.type);
        }
      } catch (err) {
        console.error('❌ Token 驗證失敗:', err);
      }

      // 驗證儲存
      console.log('🔍 驗證儲存結果...');
      console.log({
        token: !!localStorage.getItem('token'),
        userId: localStorage.getItem('userId'),
        adminRole: localStorage.getItem('adminRole'),
        adminName: localStorage.getItem('adminName'),
        adminUsername: localStorage.getItem('adminUsername')
      });

      console.log('🚀 跳轉到後台...');
      navigate('/admin/dashboard', { replace: true });

    } catch (err) {
      console.error('❌ 登入錯誤:', err);
      setError(err.message || '登入失敗，請檢查帳號密碼');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo 和標題 */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-500 rounded-full mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            後台管理系統
          </h1>
          <p className="text-gray-600">
            請使用管理員帳號登入
          </p>
        </div>

        {/* 登入表單卡片 */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 錯誤訊息 */}
            {error && (
              <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium mb-1">登入失敗</div>
                  <div className="text-sm">{error}</div>
                </div>
              </div>
            )}

            {/* 帳號輸入 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                管理員帳號
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="請輸入帳號"
                  className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  disabled={loading}
                  autoComplete="username"
                />
              </div>
            </div>

            {/* 密碼輸入 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                密碼
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="請輸入密碼"
                  className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>
            </div>

            {/* 登入按鈕 */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  登入中...
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  登入後台
                </>
              )}
            </button>
          </form>

          {/* 提示訊息 */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center">
              預設帳號：<code className="px-2 py-1 bg-gray-100 rounded text-xs">admin</code>
              <br />
              預設密碼：<code className="px-2 py-1 bg-gray-100 rounded text-xs">admin123</code>
            </p>
            <p className="mt-2 text-xs text-red-600 text-center">
              ⚠️ 請立即修改預設密碼！
            </p>
          </div>
        </div>

        {/* 返回前台連結 */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            ← 返回前台首頁
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;